import React, { FC } from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
  Font,
  Circle,
  Svg,
  Path,
} from "@react-pdf/renderer";
import { useTranslation } from "react-i18next";

// OpenLayers
import { LineString } from "ol/geom";
import OlFeature from "ol/Feature";

// Types
import { RoutePoint } from "@/ui/PublicPage/SidebarPanes/PublicRoutesPane";
import { DCRecord } from "@/@types/lib/dataController";
import { RouteWarning } from "@/@types/services/mapService";

import NorthEast from "./Icons/NorthEastIcon"
import SouthEast from "./Icons/SouthEastIcon"
import AccessTime from "./Icons/AccessTime"
import SettingsEthernet from "./Icons/SettingsEthernet"

// Register font
Font.register({
  family: "Roboto",
  fonts: [
    {
      src: "https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-light-webfont.ttf",
      fontWeight: 300,
    },
    {
      src: "https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-regular-webfont.ttf",
      fontWeight: 400,
    },
    {
      src: "https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-medium-webfont.ttf",
      fontWeight: 500,
    },
    {
      src: "https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-bold-webfont.ttf",
      fontWeight: 600,
    },
  ],
});

// Create styles
const styles = StyleSheet.create({
  text: {
    color: "#005a96",
    fontFamily: "Roboto",
  },
  textRed: {
    color: "#ff0000",
    fontFamily: "Roboto",
  },
  headerContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
  },
  logo: {
    height: 38,
    width: 38,
    marginRight: 2,
  },
  logoContainer: {
    display: "flex",
    flexDirection: "row",
  },
  pointNumbers: {
    fontFamily: "Roboto",
    color: "white",
    fontSize: 13,
    textAlign: "center",
    textOverflow: "ellipsis",
    borderStyle: "solid",
    borderRadius: "9px",
    backgroundColor: "#005a96",
    width: 18,
    height: 18,
  },
  statsContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignContent: "flex-end",
    width: "34mm",
    marginLeft: "4mm",
    flexWrap: "wrap",
  },
  statsBox: {
    width: "15mm",
    display: "flex",
    alignContent: "center",
  },
  statsValue: {
    fontSize: 14,
  },
  statsLabel: {
    fontSize: 10,
  },
  graphColumn: {
    width: "74mm",
    height: "105mm",
    display: "flex",
    justifyContent: "flex-end",
  },
  page: { 
    padding: "10mm",
    display: "flex",
    flexDirection: "column",
  },
  heading: {},
  section: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
  },
  graphImage: {
    width: "100%",
  },
  mapImage: {
    width: "152mm",
    height: "148mm",
    marginTop: "4mm",
    objectFit: "cover",
    border: "3px solid #005a96",
  },
  warningContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-end",
    height: "152mm",
    width: "34mm",
    marginLeft: "4mm",
    textAlign: "center",
  },
  warningTitle: {
    fontSize: 10,
    textTransform: "uppercase",
  },
  warning: {
    fontSize: 8,
  },
  qrCode: {
    width: "34mm",
    height: "34mm",
  },
  pointLabel: {
    fontFamily: "Roboto",
    fontSize: 13,
    marginLeft: 4,
  },
});

type RouteDocumentProps = {
  selectedRoute: {
    record: DCRecord;
    feature: OlFeature<LineString>;
  };
  routePoints: RoutePoint[];
  mapImage: string;
  qrCodeUrl: string;
  graphURI: string
};

// Create Document Component
const RouteDocument: FC<RouteDocumentProps> = (props) => {

  const { selectedRoute, routePoints, mapImage, qrCodeUrl, graphURI } = props;

  const warnings_msp = selectedRoute.record.warnings_msp as RouteWarning[];
  const warnings_podrucja = selectedRoute.record
    .warnings_podrucja as RouteWarning[];

  const { t } = useTranslation();

  return (
    <Document language="hr">
      <Page size="A4" style={styles.page}>
        <View style={[styles.section, { height: "15mm", width: "190mm", marginBottom: "5mm" }]}>
          <View style={[styles.logoContainer, {height: "15mm"}]}>
            <PdfIcon fill="#005a96" />
            <Text style={[styles.text, { fontSize: 22 }]}>
              {t("titles.route_info") + " | " + t("app_title")}
            </Text>
          </View>

          <Text style={[styles.text, { fontSize: 13, width: "41mm", textAlign: "center" }]}>
            {t("pdf.view_in_app")}
          </Text>
        </View>
        <View style={[styles.section, { justifyContent: "space-between", height: "105mm", width: "190mm" }]}>
          <View style={[{display: "flex", flexWrap: "wrap", alignContent: "space-between", gap: "3mm", width: "74mm"}]}>
            {routePoints.map((p, i) => (
              <View key={`label-${p.uuid}`} style={[{width: "74mm", display: "flex", flexDirection: "row"}]}>
                <Text style={[styles.pointNumbers]}>
                  {`${i + 1}`}
                </Text>
                <Text style={[styles.pointLabel]}>
                  {`${p.label}`}
                </Text>

              </View>
            ))}
          </View>
          <View style={[styles.graphColumn]}>
            <Image style={[styles.graphImage]} src={graphURI} />
          </View>

          <View style={[{ display: "flex", justifyContent: "space-between", height: "105mm", width: "34mm" }]}>
            {qrCodeUrl ? <Image style={[styles.qrCode]} src={qrCodeUrl}/> : null}

            <View style={[styles.statsContainer, {}]}>
              <View style={[styles.statsBox]}>
                <SettingsEthernet fill="#005a96" />
                <Text style={[styles.text, styles.statsValue]}>{`${
                  selectedRoute.record.total_length as number
                }`}</Text>
                <Text style={[styles.text, styles.statsLabel]}>{t("route.duljina") as string + " (m)"}</Text>
              </View>

              <View style={[styles.statsBox]}>
                <AccessTime fill="#005a96" />
                <Text style={[styles.text, styles.statsValue]}>{`${
                  selectedRoute.record.total_time as number
                }`}</Text>
                <Text style={[styles.text, styles.statsLabel]}>{t("route.trajanje") as string}</Text>
              </View>

              <View style={[styles.statsBox]}>
                <NorthEast fill="#005a96" />
                <Text style={[styles.text, styles.statsValue]}>{`${
                  selectedRoute.record.total_ascent as number
                } m`}</Text>
                <Text style={[styles.text, styles.statsLabel]}>
                  {t("route.elevation_gain") as string}
                </Text>
              </View>

              <View style={[styles.statsBox]}>
                <SouthEast fill="#005a96" />
                <Text style={[styles.text, styles.statsValue]}>{`${
                  selectedRoute.record.total_descent as number
                } m`}</Text>
                <Text style={[styles.text, styles.statsLabel]}>
                  {t("route.elevation_loss") as string}
                </Text>
              </View>
            </View>
          </View>
        </View>

        <View style={{height: "152mm", width: "190mm", display: "flex", flexDirection: "row"}}>
          {mapImage ? <Image style={[styles.mapImage]} src={mapImage} /> : null}

          <View>
            {Array.isArray(warnings_podrucja) ? (
              <View style={[styles.warningContainer]}>
                <Text style={[styles.warningTitle, { fontFamily: "Roboto" }]}>
                  {t("translation:titles.warning_podrucje")}
                </Text>
                {warnings_podrucja.map((wp) => (
                  <Text
                    style={[styles.warning, { fontFamily: "Roboto" }]}
                    key={wp.message_code}
                  >
                    {"-\n" + t(wp.message_ttoken)}
                  </Text>
                ))}
              </View>
            ) : null}

            {Array.isArray(warnings_msp) ? (
              <View style={[styles.warningContainer]}>
                <Text style={[styles.warningTitle, styles.textRed]}>
                  {t("translation:titles.warning_msp")}
                </Text>
                {warnings_msp.map((wm) => (
                  <Text
                    style={[styles.warning, styles.textRed]}
                    key={wm.message_code}
                  >
                    {"-\n" + t(wm.message_ttoken)}
                  </Text>
                ))}
              </View>
            ) : null}
          </View>
        </View>
      </Page>
    </Document>
  );
};

const PdfIcon = ({ fill = "#005a96", ...props }) => (
  <View style={[styles.logo]}>
    <Svg width="100%" height="100%" viewBox="0 0 100 100" {...props}>
      <Path
        d="M54.996,46.259c1.385,0.694,3.07,0.131,3.763-1.254L62.5,37.5l28.125,56.25h6.25L62.511,25l-8.769,17.496
          C53.049,43.881,53.61,45.566,54.996,46.259z"
        fill={fill || "currentColor"} // Set fill from props or default to 'currentColor'
      />
      <Circle
        cx={50}
        cy={12.5}
        r={9.375}
        fill={fill || "currentColor"} // Set fill from props or default to 'currentColor'
      />
      {/* Similarly define other paths and circles based on your SVG */}
      <Path
        d="M41.069,61.824h5.361c1.177,0,1.798,0.721,2.064,1.151c0.266,0.43,0.633,1.308,0.106,2.36L34.393,93.75h12.464
          l11.715-23.43c2.119-4.238,1.897-9.176-0.594-13.207s-6.808-6.438-11.547-6.438h-5.361c-1.177,0-1.798-0.721-2.064-1.151
          c-0.266-0.43-0.633-1.308-0.106-2.36l6.711-13.422c1.377-2.754,0.261-6.102-2.493-7.478c-2.753-1.377-6.101-0.261-7.478,2.493
          L28.928,42.18c-2.119,4.238-1.897,9.176,0.594,13.207S36.33,61.824,41.069,61.824z"
        fill={fill || "currentColor"} // Set fill from props or default to 'currentColor'
      />
      <Path
        d="M18.452,63.132L3.143,93.75h12.464l12.816-25.632c1.377-2.754,0.261-6.102-2.493-7.478
          C23.177,59.263,19.829,60.379,18.452,63.132z"
        fill={fill || "currentColor"} // Set fill from props or default to 'currentColor'
      />
      <Path
        d="M14.371,46.259c0.403,0.202,0.83,0.297,1.252,0.297c1.029,0,2.019-0.568,2.511-1.551l7.812-15.625
          c0.693-1.386,0.131-3.07-1.254-3.763c-1.385-0.693-3.07-0.131-3.763,1.254l-7.812,15.625
          C12.424,43.881,12.985,45.566,14.371,46.259z"
        fill={fill || "currentColor"} // Set fill from props or default to 'currentColor'
      />
    </Svg>
  </View>
);

export default RouteDocument;
